import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { opportunitiesAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import toast from 'react-hot-toast';
import { Eye, X, Upload, Trash2 } from 'lucide-react';

const AdminOpportunityForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [deleteBanner, setDeleteBanner] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    type: 'scholarship',
    description: '',
    organization: '',
    eligibility: [''],
    benefits: [''],
    location: { country: '', city: '', remote: false },
    applicationLink: '',
    deadline: '',
    status: 'active',
    featured: false,
  });

  useEffect(() => { if (id) fetchOpportunity(); }, [id]);

  const fetchOpportunity = async () => {
    try {
      const res = await opportunitiesAPI.getById(id!);
      const data = res.data.data;
      setFormData({
        ...data,
        deadline: data.deadline ? new Date(data.deadline).toISOString().split('T')[0] : '',
        eligibility: data.eligibility?.length ? data.eligibility : [''],
        benefits: data.benefits?.length ? data.benefits : [''],
        location: data.location || { country: '', city: '', remote: false },
      });
      if (data.bannerImage) setBannerPreview(data.bannerImage);
    } catch {
      toast.error('Failed to load opportunity');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = new FormData();
      const data = {
        ...formData,
        eligibility: formData.eligibility.filter(e => e.trim()),
        benefits: formData.benefits.filter(b => b.trim()),
      };
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'location') {
          payload.append(key, JSON.stringify(value));
        } else if (Array.isArray(value)) {
          value.forEach(v => payload.append(key, v));
        } else {
          payload.append(key, String(value ?? ''));
        }
      });
      if (bannerFile) payload.append('bannerImage', bannerFile);
      if (deleteBanner) payload.append('deleteBanner', 'true');

      if (id) {
        await opportunitiesAPI.update(id, payload);
        toast.success('Opportunity updated');
      } else {
        await opportunitiesAPI.create(data);
        toast.success('Opportunity created');
      }
      navigate('/admin/opportunities');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save opportunity');
    } finally {
      setLoading(false);
    }
  };

  const addField = (field: 'eligibility' | 'benefits') =>
    setFormData({ ...formData, [field]: [...formData[field], ''] });

  const removeField = (field: 'eligibility' | 'benefits', index: number) =>
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });

  const updateField = (field: 'eligibility' | 'benefits', index: number, value: string) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const typeColors: Record<string, string> = {
    scholarship: 'bg-blue-100 text-blue-700',
    internship: 'bg-green-100 text-green-700',
    training: 'bg-purple-100 text-purple-700',
    grant: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">{id ? 'Edit' : 'Create'} Opportunity</h1>
          <button
            type="button"
            onClick={() => setPreview(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            <Eye className="w-4 h-4" /> Preview
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <input type="text" required value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Type *</label>
              <select required value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="scholarship">Scholarship</option>
                <option value="internship">Internship</option>
                <option value="training">Training</option>
                <option value="grant">Grant</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description *</label>
            <textarea required rows={6} value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>

          {/* Banner Image */}
          <div>
            <label className="block text-sm font-medium mb-2">Banner Image</label>
            {bannerPreview && !deleteBanner ? (
              <div className="relative rounded-lg overflow-hidden mb-2">
                <img src={bannerPreview} alt="Banner" className="w-full h-48 object-cover" />
                <div className="absolute top-2 right-2 flex gap-2">
                  <label className="flex items-center gap-1 px-3 py-1.5 bg-white rounded-lg shadow text-xs font-medium cursor-pointer hover:bg-gray-50 border border-gray-200">
                    <Upload className="w-3.5 h-3.5" />
                    Change
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setBannerFile(file);
                          setBannerPreview(URL.createObjectURL(file));
                          setDeleteBanner(false);
                        }
                      }}
                    />
                  </label>
                  <button type="button"
                    onClick={() => { setDeleteBanner(true); setBannerPreview(null); setBannerFile(null); }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg shadow text-xs font-medium hover:bg-red-700">
                    <Trash2 className="w-3.5 h-3.5" />
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Click to upload banner image</span>
                <span className="text-xs text-gray-400 mt-1">JPG, PNG up to 5MB</span>
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setBannerFile(file);
                      setBannerPreview(URL.createObjectURL(file));
                      setDeleteBanner(false);
                    }
                  }}
                />
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Organization</label>
              <input type="text" value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Application Link</label>
              <input type="url" value={formData.applicationLink}
                onChange={(e) => setFormData({ ...formData, applicationLink: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Eligibility Criteria</label>
            {formData.eligibility.map((item, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input type="text" value={item}
                  onChange={(e) => updateField('eligibility', index, e.target.value)}
                  className="flex-1 px-4 py-2 border rounded-lg" placeholder="e.g., Must be African citizen" />
                <button type="button" onClick={() => removeField('eligibility', index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg">Remove</button>
              </div>
            ))}
            <button type="button" onClick={() => addField('eligibility')}
              className="text-blue-600 text-sm hover:underline">+ Add Eligibility</button>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Benefits</label>
            {formData.benefits.map((item, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input type="text" value={item}
                  onChange={(e) => updateField('benefits', index, e.target.value)}
                  className="flex-1 px-4 py-2 border rounded-lg" placeholder="e.g., Full tuition coverage" />
                <button type="button" onClick={() => removeField('benefits', index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg">Remove</button>
              </div>
            ))}
            <button type="button" onClick={() => addField('benefits')}
              className="text-blue-600 text-sm hover:underline">+ Add Benefit</button>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Country</label>
              <input type="text" value={formData.location.country}
                onChange={(e) => setFormData({ ...formData, location: { ...formData.location, country: e.target.value } })}
                className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">City</label>
              <input type="text" value={formData.location.city}
                onChange={(e) => setFormData({ ...formData, location: { ...formData.location, city: e.target.value } })}
                className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="flex items-center gap-2 mt-8">
                <input type="checkbox" checked={formData.location.remote}
                  onChange={(e) => setFormData({ ...formData, location: { ...formData.location, remote: e.target.checked } })}
                  className="w-4 h-4" />
                <span className="text-sm">Remote</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Deadline</label>
              <input type="date" value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 mt-8">
                <input type="checkbox" checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4" />
                <span className="text-sm">Featured</span>
              </label>
            </div>
          </div>

          <div className="flex gap-4">
            <button type="submit" disabled={loading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Saving...' : id ? 'Update Opportunity' : 'Save Opportunity'}
            </button>
            <button type="button" onClick={() => navigate('/admin/opportunities')}
              className="bg-gray-200 px-6 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
          </div>
        </form>
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold">Preview</h2>
              <button onClick={() => setPreview(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${typeColors[formData.type] || 'bg-gray-100 text-gray-700'}`}>
                  {formData.type}
                </span>
                {formData.featured && <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">Featured</span>}
                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${formData.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {formData.status}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{formData.title || 'Untitled'}</h1>
              {formData.organization && <p className="text-blue-600 font-medium">{formData.organization}</p>}
              {formData.description && <p className="text-gray-700 whitespace-pre-wrap">{formData.description}</p>}

              {formData.eligibility.filter(e => e.trim()).length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Eligibility Criteria</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {formData.eligibility.filter(e => e.trim()).map((e, i) => (
                      <li key={i} className="text-gray-700 text-sm">{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {formData.benefits.filter(b => b.trim()).length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Benefits</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {formData.benefits.filter(b => b.trim()).map((b, i) => (
                      <li key={i} className="text-gray-700 text-sm">{b}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-2 border-t text-sm text-gray-600">
                {(formData.location.country || formData.location.city) && (
                  <div><span className="font-medium">Location:</span> {[formData.location.city, formData.location.country].filter(Boolean).join(', ')}{formData.location.remote && ' (Remote)'}</div>
                )}
                {formData.deadline && (
                  <div><span className="font-medium">Deadline:</span> {new Date(formData.deadline).toLocaleDateString()}</div>
                )}
                {formData.applicationLink && (
                  <div className="col-span-2">
                    <span className="font-medium">Apply at:</span>{' '}
                    <a href={formData.applicationLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{formData.applicationLink}</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOpportunityForm;
